import type { AuditEntry, Case } from '../domain/initial-cases.js';
import { apiFetch } from './client.js';

export interface AuditOverride {
  code: string;
  detail?: Record<string, unknown>;
}

export async function listCases(limit = 50): Promise<Case[]> {
  const res = await apiFetch<{ items: Case[]; total: number }>(`/cases?limit=${limit}`);
  return res.items;
}

// H-2: server-side search / filters / pagination over the full RLS-scoped
// population. `total` is the filtered count, not the page size — render it
// ("showing N of M") instead of discarding it.
export interface CaseListParams {
  q?: string;
  status?: string;
  cureStatus?: string;
  revetStatus?: string;
  settlementSyncStatus?: string;
  notification?: 'pending' | 'sent';
  hasEid?: boolean;
  hasFinalDetermination?: boolean;
  /** #399 — server-side "assigned to me" (matched on the caller's users.id). */
  assignedToMe?: boolean;
  sort?: 'createdAt' | 'revetDueAt';
  order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface CaseListPage {
  items: Case[];
  total: number;
}

export async function listCasesPage(params: CaseListParams = {}): Promise<CaseListPage> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    // `false` booleans are omitted: the API treats hasEid/hasFinalDetermination
    // as presence filters and ignores false anyway.
    if (v !== undefined && v !== '' && v !== false) qs.set(k, String(v));
  }
  const suffix = qs.size > 0 ? `?${qs.toString()}` : '';
  return apiFetch<CaseListPage>(`/cases${suffix}`);
}

export interface CaseStatsPayload {
  total: number;
  inReview: number;
  successful: number;
  unsuccessful: number;
  pendingNotification: number;
  cureWindowOpen: number;
  appealsActive: number;
  revetsDue: number;
  pendingSync: number;
  synced: number;
  onboardingDraft: number;
  onboardingSubmitted: number;
  activeShaftDisputes: number;
  activeIpDisputes: number;
  // #472 — PENDING counts for the three queues on the Intake Queue page.
  intakePending: number;
  tnChangeRequestsPending: number;
  companyChangesPending: number;
}

export async function getCaseStats(): Promise<CaseStatsPayload> {
  return apiFetch<CaseStatsPayload>('/cases/stats');
}

export async function getCase(id: string): Promise<Case> {
  return apiFetch<Case>(`/cases/${id}`);
}

export async function getCaseAudit(id: string, limit = 200): Promise<AuditEntry[]> {
  const res = await apiFetch<{ items: AuditEntry[]; total: number }>(
    `/cases/${id}/audit?limit=${limit}`,
  );
  return res.items;
}

// `registrant` is null for audit rows that are not about a case — intake
// submissions (`SUB-…`), TN change requests (`TCR-…`), company profile change
// requests (`CCR-…`, #432) and the `SYSTEM:*` sentinels all live in the feed but
// have no cases row to enrich from (#311). The closed list of these namespaces
// lives in `CASELESS_CASE_ID_PREFIXES` in apps/api/src/routes/audit.ts; a
// prefix missing from THERE is dropped from the feed entirely rather than
// arriving here unlabelled.
export type GlobalAuditEntry = AuditEntry & { caseId: string; registrant: string | null };

export async function listAudit(limit = 200): Promise<GlobalAuditEntry[]> {
  const res = await apiFetch<{ items: GlobalAuditEntry[]; total: number }>(
    `/audit?limit=${limit}`,
  );
  return res.items;
}

export async function createCaseApi(
  payload: Partial<Case> & { action?: AuditOverride },
  idempotencyKey: string,
): Promise<Case> {
  return apiFetch<Case>('/cases', {
    method: 'POST',
    headers: { 'Idempotency-Key': idempotencyKey },
    body: JSON.stringify(payload),
  });
}

// R2/B-3: requests a settlement sync — the API enqueues the worker job and
// emits SETTLEMENT_SYNC_REQUESTED. The case stays PENDING_SYNC until the
// worker confirms with the platform; the client never writes SYNCED.
export async function requestSettlementSync(
  id: string,
): Promise<{ jobId: string | null; settlementSyncStatus: string }> {
  return apiFetch<{ jobId: string | null; settlementSyncStatus: string }>(
    `/cases/${id}/settlement-sync`,
    { method: 'POST' },
  );
}

export async function patchCase(
  id: string,
  version: number,
  patch: Partial<Case> & { action?: AuditOverride },
): Promise<Case> {
  return apiFetch<Case>(`/cases/${id}`, {
    method: 'PATCH',
    headers: { 'If-Match': String(version) },
    body: JSON.stringify(patch),
  });
}

export async function deleteCase(id: string, version: number): Promise<void> {
  await apiFetch<null>(`/cases/${id}`, {
    method: 'DELETE',
    headers: { 'If-Match': String(version) },
  });
}

// #514 — link a case to a customer. Its own endpoint, not a PATCH: PATCH
// replaces `onboarding` wholesale and would erase everything else in it.
export async function attachCaseCustomer(
  id: string,
  customerId: string,
): Promise<{ id: string; customerId: string; version: number }> {
  return apiFetch<{ id: string; customerId: string; version: number }>(
    `/cases/${encodeURIComponent(id)}/customer`,
    { method: 'POST', body: JSON.stringify({ customerId }) },
  );
}

// #776 — the analyst recommendation. Its own endpoint, not a PATCH: the API
// takes the recommender from the caller's sign-in and chooses the audit code
// itself, so nothing here names a person. If-Match like PATCH, because a
// recommendation is what a decision is taken against; both return the case.
export async function postRecommendation(
  id: string,
  version: number,
  recommendation: 'APPROVE' | 'DENY',
  note?: string,
): Promise<Case> {
  return apiFetch<Case>(`/cases/${encodeURIComponent(id)}/recommendation`, {
    method: 'POST',
    headers: { 'If-Match': String(version) },
    body: JSON.stringify(note ? { recommendation, note } : { recommendation }),
  });
}

// #766 — the case's OA/VA Agent has opened it, which clears its "New" marker on
// the Cases list. No If-Match, and the version does not move: it is a marker,
// not an edit, so it cannot 409 the agent's own PATCH chain. 403 for anyone
// but the assigned agent.
export async function markVettingOpened(
  id: string,
): Promise<{ id: string; vettingOpenedAt: string; version: number }> {
  return apiFetch<{ id: string; vettingOpenedAt: string; version: number }>(
    `/cases/${encodeURIComponent(id)}/vetting-opened`,
    { method: 'POST' },
  );
}

export async function deleteRecommendation(id: string, version: number): Promise<Case> {
  return apiFetch<Case>(`/cases/${encodeURIComponent(id)}/recommendation`, {
    method: 'DELETE',
    headers: { 'If-Match': String(version) },
  });
}
